import React, { ReactNode } from 'react';
import { ReactComponent as TopWave } from '../../assets/svgs/product/product-steps-top.svg';
import { ReactComponent as BottomWave } from '../../assets/svgs/product/product-steps-bottom.svg';
import { useIntl } from 'react-intl';
import { MonoFontLabel, RecoletaHeading } from '../common/typography';
import { ReactComponent as Underline } from '../../assets/svgs/product/underline-yellow.svg';
import { markdownText } from '../../pages/doctors';
import { ButtonLink } from '../common/Button';
import { SYSTEM_FONTS } from '../../styles/typography';
import { ReactComponent as Step1 } from '../../assets/svgs/product/img-step-1.svg';
import { ReactComponent as Step2 } from '../../assets/svgs/product/img-step-2.svg';
import { ReactComponent as Step3 } from '../../assets/svgs/product/img-step-3.svg';
import { ReactComponent as Hotline } from '../../assets/svgs/product/img-hotline.svg';
import presets from '../../styles/presets';
import {
  CTA_OPEN_APP_STORE_LINK,
  HUBSPOT_MEETING_MANU_LINK,
  REQUEST_MATERIALS_HUBSPOT_FORM_FORM_ID,
  REQUEST_MATERIALS_HUBSPOT_FORM_PORTAL_ID,
  REZEPT_URL,
} from '../../utils/constants';
import HubspotOverlay from '../../components/HubspotFormOverlay';
import HubspotMeetingOverlay from '../../components/HubspotMeetingOverlay';

export const StepsCard = ({
  title,
  description,
  button,
  svg,
}: {
  title: string;
  description: string;
  button: ReactNode;
  svg: ReactNode;
}) => (
  <>
    <div
      css={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '90%',
        marginBottom: 15,
        marginTop: 15,
        position: 'relative',

        [presets.lg]: {
          width: '70%',
        },
      }}
    >
      <div
        css={{
          display: 'none',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: 24,
          zIndex: 200,
          position: 'relative',

          [presets.md]: {
            display: 'flex',
          },
        }}
      >
        {svg}
      </div>

      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          padding: 24,
          borderRadius: 8,
          width: '100%',
        }}
      >
        <p
          css={{
            fontFamily: ['Blacker Pro Display'].concat(SYSTEM_FONTS).join(', '),
            fontWeight: 500,
            fontSize: 28,
            color: '#000',
            width: '100%',
          }}
        >
          {markdownText(title)}
        </p>
        <p
          css={{
            fontFamily: ['Hero New'].concat(SYSTEM_FONTS).join(', '),
            fontWeight: 400,
            fontSize: 16,
            color: '#000',
          }}
        >
          {markdownText(description)}
        </p>

        {button}
      </div>
    </div>
  </>
);

function Steps() {
  const intl = useIntl();

  const steps = [
    {
      title: intl.formatMessage({
        id: 'product.page.steps.step1.title',
        defaultMessage:
          '<b style="color: #3D72DF">Ärztliche Verschreibung</b> erhalten',
      }),
      description: intl.formatMessage({
        id: 'product.page.steps.step1.text',
        defaultMessage:
          'Alle Ärzt*innen* sowie Psychotherapeut*innen* können dir **Cara Care für Reizdarm** auf Rezept verschreiben.\n\nWir unterstützen dich dabei! Vereinbare einen **Termin** bei deinem Arzt oder deiner Ärztin und lege das **Infomaterial für Ärzt*innen* **vor.',
      }),
      svg: <Step1 />,
      button: (
        <HubspotOverlay
          formId={REQUEST_MATERIALS_HUBSPOT_FORM_FORM_ID}
          portalId={REQUEST_MATERIALS_HUBSPOT_FORM_PORTAL_ID}
          buttonText={intl.formatMessage({
            id: 'product.page.steps.step1.cta.text',
            defaultMessage: 'InfoMaterial Erhalten',
          })}
        />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'product.page.steps.step2.title',
        defaultMessage:
          '<b style="color: #3D72DF">Rezept einreichen</b> & <b style="color: #3D72DF">Freischaltcode erhalten</b>',
      }),
      description: intl.formatMessage({
        id: 'product.page.steps.step2.text',
        defaultMessage:
          "Rezept erhalten? <b>Mache ein Foto davon</b> und reiche es <a target='_blank' href='#'>HIER</a> direkt ein oder schicke es an <a target='_blank' href='#'>rezept@cara.care</a>.\n\nDein Zugangscode sollte nach ca. 14 Tagen in deinem Briefkasten landen. Privat versichert? Wende dich an uns!",
      }),
      svg: <Step2 />,
      button: (
        <ButtonLink
          design="bordered"
          css={{
            color: '#FFFFFF',
            fontWeight: 700,
            backgroundColor: '#2AAB3C',
          }}
          to={REZEPT_URL}
          target="_blank"
        >
          {intl.formatMessage({
            id: 'product.page.steps.step2.cta.text',
            defaultMessage: 'Rezept Einreichen',
          })}
        </ButtonLink>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'product.page.steps.step3.title',
        defaultMessage:
          '<b style="color: #3D72DF">App installieren</b> & <b style="color: #3D72DF">Programm beginnen</b>',
      }),
      description: intl.formatMessage({
        id: 'product.page.steps.step3.text',
        defaultMessage:
          '<b>Lade dir <i>Cara Care für Reizdarm</i></b> im App Store bzw. Google Play Store herunter, <b>registriere dich</b> neu und <b>gib deinen Zugangscode ein</b>.\n\nNachdem du die einleitenden Artikel und den <b>Gesundheitsfragebogen</b> abgeschlossen hast, erhältst du vollen Zugang zu deinem individualisierten Programm.',
      }),
      svg: <Step3 />,
      button: (
        <ButtonLink
          design="bordered"
          css={{
            color: '#FFFFFF',
            fontWeight: 700,
            backgroundColor: '#2AAB3C',
          }}
          to={CTA_OPEN_APP_STORE_LINK}
          target="_blank"
        >
          {intl.formatMessage({
            id: 'product.page.steps.step3.cta.text',
            defaultMessage: 'App Herunterladen',
          })}
        </ButtonLink>
      ),
    },
  ];

  return (
    <div css={{ marginBottom: 48 }}>
      <TopWave
        width="100%"
        height="43"
        css={{
          display: 'block',
          width: '100%',
          height: 'auto',
          marginTop: -1,
          position: 'relative',
          zIndex: 1,
          color: '#235544',
        }}
      />
      <div
        css={{
          marginTop: -1,
          backgroundColor: '#235544',
          position: 'relative',
          zIndex: 0,
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Content */}
          <MonoFontLabel
            css={{
              marginTop: '2rem',
              fontWeight: 700,
              fontSize: 12,
              lineHeight: '19px',
              marginRight: 10,
              letterSpacing: 3,
              color: '#ffffff90',
              textAlign: 'center',
            }}
          >
            {intl.formatMessage({
              id: 'product.page.steps.prefix',
              defaultMessage: 'Dein Weg zu Cara Care für Reizdarm',
            })}
          </MonoFontLabel>
          <RecoletaHeading
            css={{
              color: '#fff',
              fontWeight: 500,
              fontSize: '32px',
              padding: '0 50px',
              textAlign: 'center',

              [presets.md]: {
                padding: '0 20px',
              },
            }}
          >
            {intl.formatMessage({
              id: 'product.page.steps.pre.title',
              defaultMessage: 'in',
            })}{' '}
            <span css={{ position: 'relative', color: '#C6D13E' }}>
              <Underline
                css={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                }}
              />
              {intl.formatMessage({
                id: 'product.page.steps.underline.title',
                defaultMessage: '3 Schritten',
              })}
            </span>{' '}
            {intl.formatMessage({
              id: 'product.page.steps.post.title',
              defaultMessage: 'zu Frieden mit deinem Bauch',
            })}
          </RecoletaHeading>
          {/* Steps */}
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 48,
              marginBottom: 30,
              width: '100%',
              maxWidth: 1200,
            }}
          >
            {steps.map((step, index) => (
              <StepsCard
                key={index}
                title={step.title}
                description={step.description}
                svg={step.svg}
                button={step.button}
              />
            ))}
          </div>

          {/* Hotline */}
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              marginBottom: '3rem',

              [presets.md]: {
                marginBottom: '2rem',
              },
            }}
          >
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                flexDirection: 'row',
              }}
            >
              <Hotline />
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: 10,
                  textAlign: 'center',
                }}
              >
                <p
                  css={{
                    fontFamily: ['Blacker Pro Display']
                      .concat(SYSTEM_FONTS)
                      .join(', '),
                    fontWeight: 500,
                    fontSize: 22,
                    color: '#fff',
                    maxWidth: 400,
                    marginBottom: 2,
                  }}
                >
                  {intl.formatMessage({
                    id: 'homepage.v2.feature.next.steps.cta.title',
                    defaultMessage: 'Weitere Fragen?',
                  })}
                </p>
                <p
                  css={{
                    fontFamily: ['Hero New'].concat(SYSTEM_FONTS).join(', '),
                    fontWeight: 400,
                    fontSize: 16,
                    color: '#ffffff80',
                    maxWidth: 200,
                    marginBottom: 0,
                  }}
                >
                  {intl.formatMessage({
                    id: 'homepage.v2.feature.next.steps.cta.text',
                    defaultMessage:
                      'Vereinbare einfach ein Infogespräch mit uns.',
                  })}
                </p>
              </div>
            </div>
            <HubspotMeetingOverlay
              meetingLink={HUBSPOT_MEETING_MANU_LINK || ''}
              buttonText={intl.formatMessage({
                id: 'homepage.v2.feature.next.steps.cta.button',
                defaultMessage: 'Gespräch Buchen',
              })}
            />
          </div>
        </div>
      </div>
      <div className="hubspot-overlay">
        <div className="hubspot-overlay-close"></div>
        <div className="hubspot-overlay-content"></div>
      </div>
      <BottomWave
        css={{
          display: 'block',
          width: '100%',
          height: 'auto',
          marginTop: -1,
          position: 'relative',
          zIndex: 0,
          color: '#235544',
        }}
      />
    </div>
  );
}

export default Steps;
